import { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { api } from '../../api/api';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, Select, MenuItem, FormControl,
  InputLabel, Button, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import HistoryIcon from '@mui/icons-material/History';
import toast from 'react-hot-toast';
import { Tabs, Tab } from '@mui/material';
import AuditTable from './AuditTable';

const ACTION_LABELS = {
  store_created: { label: 'Store Created', color: '#22c55e', bg: '#f0fdf4' },
  store_deleted: { label: 'Store Deleted', color: '#ef4444', bg: '#fef2f2' },
  screen_config: { label: 'Screen Config', color: '#3b82f6', bg: '#eff6ff' },
  price_init: { label: 'Price Init', color: '#f59e0b', bg: '#fffbeb' },
  tier_changed: { label: 'Tier Changed', color: '#8b5cf6', bg: '#faf5ff' },
  setup_completed: { label: 'Setup Done', color: '#22c55e', bg: '#f0fdf4' },
  rollback: { label: 'Rollback', color: '#ef4444', bg: '#fef2f2' },
};

function ActivityLog({ user }) {
  const [view, setView] = useState('audit'); // 작업 기록(감사) | 설정 기록(setup-log)
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [search, setSearch] = useState('');
  const [detailDialog, setDetailDialog] = useState(null);
  const [rollbackDialog, setRollbackDialog] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/setup-log?limit=200');
      setLogs(res.data.logs || []);
    } catch (e) { toast.error('Failed to load logs'); }
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, []);

  const handleRollback = async (logId) => {
    try {
      await api.post(`/setup-log/rollback/${logId}`);
      toast.success('Rolled back successfully');
      setRollbackDialog(null);
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Rollback failed');
    }
  };

  const filtered = logs.filter(l => {
    if (filterAction && l.action !== filterAction) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(l.storeName?.toLowerCase().includes(q) || l.actorName?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '-';

  const viewTabs = (
    <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ mb: 2, borderBottom: '1px solid #e2e8f0' }}>
      <Tab value="audit" label="작업 기록" />
      <Tab value="setup" label="설정 기록" />
    </Tabs>
  );
  if (view === 'audit') {
    return (
      <Layout title="Activity Log" user={user}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <HistoryIcon sx={{ color: '#551FFF' }} />
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>Activity Log</Typography>
          </Box>
          {viewTabs}
          <AuditTable />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Activity Log" user={user}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: 2 }}>
        {viewTabs}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon sx={{ color: '#551FFF' }} />
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>Activity Log</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" placeholder="Search..." value={search}
              onChange={e => setSearch(e.target.value)} sx={{ width: 180 }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Action</InputLabel>
              <Select value={filterAction} label="Action" onChange={e => setFilterAction(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {Object.entries(ACTION_LABELS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Actor</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Store</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#aaa' }}>No activity logs</TableCell></TableRow>
                ) : filtered.map(log => {
                  const actionInfo = ACTION_LABELS[log.action] || { label: log.action, color: '#888', bg: '#f5f5f5' };
                  return (
                    <TableRow key={log._id} hover sx={{ opacity: log.rolledBack ? 0.5 : 1 }}>
                      <TableCell sx={{ fontSize: 12 }}>{fmtDate(log.createdAt)}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{log.actorName || '-'}</Typography>
                        <Typography sx={{ fontSize: 10, color: '#888' }}>{log.actorRole}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={actionInfo.label} size="small" sx={{ background: actionInfo.bg, color: actionInfo.color, fontWeight: 600, fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 500 }}>{log.storeName || log.store?.name || '-'}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: '#555', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.details ? (typeof log.details === 'string' ? log.details : JSON.stringify(log.details).slice(0, 80)) : '-'}
                      </TableCell>
                      <TableCell>
                        {log.rolledBack ? (
                          <Chip label="Rolled Back" size="small" sx={{ background: '#fef2f2', color: '#ef4444', fontSize: 10 }} />
                        ) : (
                          <Chip label="Active" size="small" sx={{ background: '#f0fdf4', color: '#22c55e', fontSize: 10 }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" sx={{ fontSize: 10, minWidth: 0 }}
                          onClick={() => setDetailDialog(log)}>Detail</Button>
                        {!log.rolledBack && user?.role === 'admin' && (
                          <Button size="small" color="error" startIcon={<UndoIcon sx={{ fontSize: 12 }} />}
                            sx={{ fontSize: 10, minWidth: 0 }}
                            onClick={() => setRollbackDialog(log)}>Rollback</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Detail Dialog */}
        <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Log Detail</DialogTitle>
          <DialogContent>
            {detailDialog && (
              <Box sx={{ fontSize: 13 }}>
                <p><strong>Date:</strong> {new Date(detailDialog.createdAt).toLocaleString()}</p>
                <p><strong>Actor:</strong> {detailDialog.actorName} ({detailDialog.actorRole})</p>
                <p><strong>Action:</strong> {detailDialog.action}</p>
                <p><strong>Store:</strong> {detailDialog.storeName}</p>
                <p><strong>Details:</strong></p>
                <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, fontSize: 11, overflow: 'auto', maxHeight: 300 }}>
                  {JSON.stringify(detailDialog.details, null, 2)}
                </pre>
                {detailDialog.snapshot && (
                  <>
                    <p><strong>Snapshot (rollback data):</strong></p>
                    <pre style={{ background: '#fef2f2', padding: 12, borderRadius: 8, fontSize: 11, overflow: 'auto', maxHeight: 200 }}>
                      {JSON.stringify(detailDialog.snapshot, null, 2)}
                    </pre>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions><Button onClick={() => setDetailDialog(null)}>Close</Button></DialogActions>
        </Dialog>

        {/* Rollback Confirm */}
        <Dialog open={!!rollbackDialog} onClose={() => setRollbackDialog(null)} maxWidth="xs">
          <DialogTitle sx={{ fontWeight: 700 }}>Confirm Rollback</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to rollback this action?</Typography>
            <Typography sx={{ fontSize: 13, color: '#888', mt: 1 }}>
              {rollbackDialog?.action}: {rollbackDialog?.storeName}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRollbackDialog(null)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={() => handleRollback(rollbackDialog._id)}>Rollback</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default ActivityLog;
